import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Container, Eyebrow, ArrowLink } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";
import { TicketTag } from "@/components/shared/EventCard";
import { Img } from "@/components/shared/Img";
import { fmtDate, fmtTime } from "@/lib/format";
import { displayFor } from "@/lib/display";
import { cn } from "@/lib/utils";

/*
 * Programme agenda for the front page: one big-type line per event on an ink band.
 * Date as a tall numeral, title set large, metadata in one quiet line. On pointer devices the
 * photograph only appears when you hover the row – it slides in beside the title – so the list
 * reads as typography first and pictures second. On touch it is a small still thumbnail.
 */

const month = (iso) => fmtDate(iso, "MMM").replace(".", "");

const AgendaRow = ({ event, first = false }) => {
  const multi = event.end_date && event.end_date !== event.date;
  return (
      <Link
        to={`/arrangement/${event.id}`}
        className="group relative -mx-3 grid grid-cols-[56px_minmax(0,1fr)_56px] items-center gap-x-3.5 rounded-[12px] px-3 py-5 transition-colors duration-300 hover:bg-paper/[0.05] focus-visible:bg-paper/[0.05] focus-visible:outline-none sm:-mx-4 sm:gap-x-4 sm:px-4 sm:py-6 md:grid-cols-[112px_minmax(0,1fr)_200px_48px] md:gap-x-8 lg:grid-cols-[132px_minmax(0,1fr)_220px_48px] lg:py-7 lg:pr-16 min-[1608px]:pr-4"
        data-testid={`event-card-${event.id}`}
      >
        {/* date – on phones the end day and month may stack («27» / «–28» / «nov») so nothing runs into the title */}
        <div className="flex flex-col items-start leading-none md:flex-row md:items-baseline md:gap-2" data-testid={`event-card-date-${event.id}`}>
          <span className="font-display text-[30px] font-semibold tracking-[-0.045em] tabular text-paper sm:text-[36px] md:text-[clamp(40px,3.4vw,62px)]">{fmtDate(event.date, "d")}</span>
          <span className="t-mono mt-1.5 flex flex-wrap gap-x-1 leading-[1.25] text-paper/55 md:mt-0 md:flex-nowrap md:whitespace-nowrap">{multi && <span className="tabular">–{fmtDate(event.end_date, "d")}</span>}<span>{month(event.date)}</span></span>
        </div>

        {/* words */}
        <div className="min-w-0">
          <p className="t-mono flex flex-wrap items-center gap-x-3 gap-y-1">
            {first && <span className="inline-flex items-center gap-2 text-paper/85"><span className="live-dot" aria-hidden="true" />Neste</span>}
            {event.category && <span className="text-sol">{event.category}</span>}
          </p>
          <h3 className="mt-2 break-words font-display text-[clamp(20px,1.25vw+13px,38px)] font-semibold leading-[1.06] tracking-[-0.025em] text-paper text-balance transition-transform duration-500 ease-house group-hover:translate-x-1.5 md:mt-2.5 md:leading-[1.04]">{event.title}</h3>
          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-paper/60 md:mt-3 md:gap-x-3.5 md:text-[14px]">
            <span className="capitalize">{multi ? `${fmtDate(event.date, "EEEE d.")}–${fmtDate(event.end_date, "d. MMM")}` : fmtDate(event.date, "EEEE")}</span>
            {(event.start_time || event.end_time) && <span className="tabular whitespace-nowrap">{fmtTime(event.start_time, event.end_time)}</span>}
            {event.location && <span className="hidden sm:inline">{event.location}</span>}
            {event.price_text && <span className="tabular text-paper/85">{event.price_text}</span>}
            {event.tickets && <TicketTag t={event.tickets} light />}
          </p>
        </div>

        {/* photograph – still thumbnail on touch, hover reveal on pointer screens */}
        <div className="relative h-14 w-14 justify-self-end md:h-[108px] md:w-full lg:h-[124px]">
          {event.image ? (
            <Img
              src={event.image}
              alt=""
              width={480}
              widths={[240, 480, 800]}
              sizes="(min-width: 1024px) 220px, (min-width: 768px) 190px, 56px"
              style={displayFor(event, event.image).fit === "contain" ? undefined : { objectPosition: `${displayFor(event, event.image).focus.x}% ${displayFor(event, event.image).focus.y}%` }}
              data-fit={displayFor(event, event.image).fit}
              className={cn(
                "h-full w-full rounded-[8px] md:rounded-[10px]",
                displayFor(event, event.image).fit === "contain" ? "object-contain" : "object-cover",
                "md:origin-left md:-rotate-2 md:scale-90 md:opacity-0 md:transition-[opacity,transform] md:duration-500 md:ease-house",
                "md:group-hover:rotate-0 md:group-hover:scale-100 md:group-hover:opacity-100 md:group-focus-visible:rotate-0 md:group-focus-visible:scale-100 md:group-focus-visible:opacity-100"
              )}
            />
          ) : (
            <div className="h-full w-full rounded-[8px] bg-paper/10 md:hidden" aria-hidden="true" />
          )}
        </div>

        {/* arrow */}
        <span className="hidden h-12 w-12 items-center justify-center rounded-full bg-paper/10 text-paper transition-[background-color,color,transform] duration-300 group-hover:scale-105 group-hover:bg-sol group-hover:text-ink md:flex">
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-px group-hover:-translate-y-px" />
        </span>
      </Link>
  );
};

export const ProgrammeAgenda = ({ events, loading = false, facebookUrl, className }) => (
  <section id="program" className={cn("grain relative scroll-mt-24 bg-ink pb-16 pt-12 text-paper sm:pb-20 sm:pt-14 lg:pb-24 lg:pt-20", className)} data-testid="home-programme">
    <Container className="relative">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-10">
        <div className="lg:col-span-7">
          <Eyebrow num="01" light>Program</Eyebrow>
          <h2 className="t-h2 mt-4 text-balance text-paper">Kva <em className="t-swash">skjer</em> på huset?</h2>
        </div>
        <p className="max-w-md text-[16px] leading-relaxed text-paper/70 text-pretty lg:col-span-5 lg:pb-1.5">Konsertar, quiz, teater og møte – med billettar rett på mobilen. Programmet vert fylt på etter kvart som styret stadfestar nye arrangement.</p>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-b border-paper/12 pb-5 sm:mt-12 lg:mt-14">
        <p className="t-mono text-paper/55" data-testid="home-events-count">{loading ? "Hentar program" : events?.length ? `${events.length} komande` : "Ingen komande"}</p>
        <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
          <ArrowLink to="/arrangement" light data-testid="home-all-events-link">Sjå heile programmet</ArrowLink>
          <ArrowLink to="/arranger" light data-testid="home-programme-arranger-link">Arranger noko sjølv</ArrowLink>
          {facebookUrl && <ArrowLink light href={facebookUrl} target="_blank" rel="noopener noreferrer" up data-testid="home-facebook-link">Følg oss på Facebook</ArrowLink>}
        </div>
      </div>

      <div>
        {loading ? (
          <div className="space-y-2 pt-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-[12px] bg-paper/10" />)}</div>
        ) : events?.length ? (
          <div className="divide-y divide-paper/12 border-b border-paper/12" data-testid="home-events">
            {events.map((e, i) => (
              <FadeUp key={e.id} delay={i * 0.05}><AgendaRow event={e} first={i === 0} /></FadeUp>
            ))}
          </div>
        ) : (
          <div className="pt-8" data-testid="home-events-empty">
            <p className="t-h3 text-paper">Det er ingen komande arrangement publiserte akkurat no.</p>
            <p className="mt-2 max-w-xl text-[15px] text-paper/70">Følg oss på Facebook for siste nytt – eller book huset til noko du vil få til sjølv.</p>
          </div>
        )}
      </div>
    </Container>
  </section>
);
